/**
 * feat-270 — one pending item, rendered.
 *
 * Three treatments, and **which one an item gets is decided by `change_kind`
 * alone** (spec AT-26b). This is the file where that rule is either kept or
 * broken:
 *
 *   `deleted` → the REMOVAL panel. One column, the removed content, and Restore.
 *               A deletion is immediate (spec D5), so there is no "on phones now"
 *               side — it is already gone.
 *   `created` → one column. Every `approved_value` is null (ADR-B §3.2 ruling 3),
 *               and per NOTES §10.8 a never-approved item is NOT diffed against
 *               null: "On phones now: —" on every field would assert the learner
 *               is reading a blank, when the truth is they are not reading the
 *               item at all.
 *   `updated` → the ordinary two-column diff.
 *
 * ⚠ **AT-26b — the trap this file exists to not fall into.** Removing a rehearsal
 * CRITERION arrives as `change_kind: "updated"`, because a criterion is not one
 * of the four independently-deletable item types — it is a *value* of the
 * rehearsal (ADR-A §5.1; criterion ids are regenerated on every save). So it gets
 * the ordinary two-column diff, with **no Restore control**, and learners keep
 * being graded on the approved rubric until the lesson is approved.
 *
 * **Nothing in the payload steers you toward the removal panel** — which is
 * exactly why it needs saying. The mistake is the well-meaning one: you notice
 * the criteria list got shorter and reach for the treatment that already exists
 * for "something was removed". The removal panel's sentence, "Removed from phones
 * — already gone", is **false** on a criterion, and false in the direction that
 * makes an approver relax. **Branch on `change_kind`, never on the list length**
 * — and there is deliberately no list-length comparison anywhere in this file.
 *
 * ⚠ **No comparison of any kind here.** `fields[].changed` is the server's
 * (ADR-B §2.1, spec AT-13b) and is rendered verbatim. The values arrive RAW, so a
 * changed row's two sides may look identical on screen — that is correct and is
 * not a bug to "fix" with a client-side check.
 */

import React, { useState } from 'react';
import {
  CHANGED_FIELD_ROW_CLASS,
  ChangedDot,
  DIFF_MARKER_CLASS,
} from '../../curriculumDiffPresentation';
import { APPROVAL_QUEUE_COPY as C } from '../approvalCopy';
import { ANSWER_KEY_FIELDS, sortFieldsAnswerKeyFirst } from '../approvalSelection';
import { QUIZ_TERMS } from '../../quizTerminology';
import { timeAgo } from '../relativeTime';
import { CriteriaList } from './criteriaValue';
import {
  actorDisplayName,
  type ApprovalField,
  type ApprovalPendingItem,
} from '../../../../services/curriculumApprovalService';

/** NOTES §10.6 — both sides clamp to the SAME height and expand TOGETHER. */
const CLAMP_LINES = 6;

/** Long enough to be worth clamping. Below this the expander is noise. */
function isLong(value: unknown): boolean {
  return typeof value === 'string' && value.split('\n').length > CLAMP_LINES;
}

function entityTerm(entityType: string): string {
  switch (entityType) {
    case 'question_item':
      return QUIZ_TERMS.question.singular;
    case 'rehearsal_template':
      return QUIZ_TERMS.rehearsal.singular;
    case 'video_item':
      return QUIZ_TERMS.video.singular;
    case 'reading_item':
      return QUIZ_TERMS.reading.singular;
    case 'learn_drill':
      return QUIZ_TERMS.drill.singular;
    case 'learn_lesson':
      return QUIZ_TERMS.lesson.singular;
    default:
      return entityType;
  }
}

/**
 * Renders one stored value.
 *
 * ⚠ This is a RENDERER, never a comparator. It is the thing ADR-B §2.1 warns
 * about by name — `formatSnapshotValue`-as-a-comparator is how a client-side
 * comparison walks back in — so its output must never be fed to an equality
 * check. Nothing in this file does that, and nothing added to it may.
 */
const FieldValue: React.FC<{
  value: unknown;
  expanded: boolean;
}> = ({ value, expanded }) => {
  if (Array.isArray(value)) {
    // Rehearsal criteria — a numbered SEQUENCE, never a bag. See `criteriaValue`.
    return <CriteriaList value={value} />;
  }
  const clamp =
    !expanded && isLong(value)
      ? {
          display: '-webkit-box',
          WebkitLineClamp: CLAMP_LINES,
          WebkitBoxOrient: 'vertical' as const,
        }
      : undefined;
  return (
    <span
      className="break-words whitespace-pre-wrap overflow-hidden block"
      style={clamp}
    >
      {value === null || value === undefined || value === '' ? (
        // §10.7: a sentence asserts knowledge, a dash asserts ignorance. The two
        // are easy to swap by accident and they are the mirror of each other.
        <em className="text-inkMuted">{C.emptyApprovedValue}</em>
      ) : (
        String(value)
      )}
    </span>
  );
};


/** One field on one side of the diff. */
const DiffField: React.FC<{
  field: ApprovalField;
  side: 'approved' | 'draft';
  expanded: boolean;
  onToggleExpand: () => void;
}> = ({ field, side, expanded, onToggleExpand }) => {
  const value = side === 'approved' ? field.approved_value : field.draft_value;
  const isAnswerKey = ANSWER_KEY_FIELDS.has(field.field);
  const longEither = isLong(field.approved_value) || isLong(field.draft_value);

  return (
    <div className={`min-w-0 ${field.changed ? CHANGED_FIELD_ROW_CLASS : ''}`}>
      <dt className="text-[10px] uppercase tracking-wide text-inkMuted flex items-center gap-1 flex-wrap">
        {/* Server-computed, and it is the FIELD'S OWN label — `correct_option`
            prints as "Correct answer" because that is what the <label> the editor
            is looking at says (ADR-B §3.2 ruling 5). */}
        {field.label}
        {field.changed && <ChangedDot />}
        {field.changed && <span className={DIFF_MARKER_CLASS}>{C.differs}</span>}
        {isAnswerKey && (
          // NOTES §5: a CATEGORY marker, deliberately not an alarm — the editor
          // may well be correcting a genuine error. The ORDERING does the work;
          // this only explains why the row is on top.
          <span className="text-[9px] font-bold uppercase tracking-wide rounded px-1 py-0.5 bg-cream-dark text-inkMuted">
            {C.answerKeyTag}
          </span>
        )}
      </dt>
      <dd className="text-xs text-ink">
        <FieldValue value={value} expanded={expanded} />
        {longEither && (
          // Expansion is IN PLACE, never a tooltip: a touch user cannot open one
          // and a keyboard user cannot hold one (§10.6).
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-[11px] font-semibold text-coral hover:underline mt-1"
          >
            {expanded
              ? C.showFewerLines
              : C.showAllLines(String(field.draft_value ?? field.approved_value ?? '').split('\n').length)}
          </button>
        )}
      </dd>
    </div>
  );
};

export interface ApprovalItemDiffProps {
  item: ApprovalPendingItem;
  /** Restore is the EXISTING revert route; absent unless the server says so. */
  onRestore?: (item: ApprovalPendingItem) => void;
  restoreBusy?: boolean;
}

export const ApprovalItemDiff: React.FC<ApprovalItemDiffProps> = ({
  item,
  onRestore,
  restoreBusy,
}) => {
  // One expansion state for the WHOLE item, which is what makes both sides
  // expand together (§10.6). Per-side state is the bug that rule names.
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((v) => !v);

  const fields = sortFieldsAnswerKeyFirst(item.fields);
  const term = entityTerm(item.entity_ref.entity_type);

  // ── THE BRANCH. `change_kind`, and nothing else. ────────────────────────────
  if (item.change_kind === 'deleted') {
    return (
      <div className="mt-2.5 rounded-xl border border-softBorder bg-cream-dark/60 px-3 py-2.5 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wide text-inkMuted mb-1.5">
          {C.removedHeading}
        </div>
        <dl className="space-y-1.5">
          {/* Ruling 1: `fields` carries every gated column, changed or not, so
              the removal panel can name what was removed WITHOUT a second round
              trip — and it renders them with no change marker. */}
          {fields.map((f) => (
            <div key={f.field} className="min-w-0">
              <dt className="text-[10px] uppercase tracking-wide text-inkMuted">{f.label}</dt>
              <dd className="text-xs text-ink">
                <FieldValue value={f.approved_value} expanded={expanded} />
              </dd>
            </div>
          ))}
        </dl>
        {/* `restorable` is evaluated server-side, so the UI never offers a button
            the API will refuse — a UI-only guard is not a guard, but neither is a
            UI that offers an action the server rejects (ADR-B §3.2 ruling 4). */}
        {item.restorable && item.history_id && onRestore && (
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              disabled={restoreBusy}
              onClick={() => onRestore(item)}
              className="text-xs font-semibold text-coral hover:underline flex items-center gap-1 disabled:opacity-50"
            >
              {C.restore(term)}
            </button>
            <span className="text-[11px] text-inkMuted">{C.restoreNote}</span>
          </div>
        )}
      </div>
    );
  }

  if (item.change_kind === 'created') {
    // §10.8 — never-approved skips field diffing ENTIRELY rather than diffing
    // against null. There is no approved snapshot, so per-field divergence is not
    // empty, it is MEANINGLESS.
    return (
      <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wide text-inkMuted mb-1.5">
            {C.approvedColumn}
          </div>
          <div className="text-xs text-inkMuted italic">{C.notOnPhones}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wide text-inkMuted mb-1.5">
            {C.draftColumn}
          </div>
          <dl className="space-y-1.5">
            {fields.map((f) => (
              <div key={f.field} className="min-w-0">
                <dt className="text-[10px] uppercase tracking-wide text-inkMuted">{f.label}</dt>
                <dd className="text-xs text-ink">
                  <FieldValue value={f.draft_value} expanded={expanded} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    );
  }

  // ── `updated` — the ordinary two-column diff, and ALSO the criterion case. ──
  const criteriaField = fields.find((f) => f.field === 'criteria' && f.changed);
  const approvedCriteriaCount = Array.isArray(criteriaField?.approved_value)
    ? (criteriaField!.approved_value as unknown[]).length
    : null;

  return (
    <>
      <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wide text-inkMuted mb-1.5">
            {C.approvedColumn}
          </div>
          <dl className="space-y-1.5">
            {fields.map((f) => (
              <DiffField
                key={f.field}
                field={f}
                side="approved"
                expanded={expanded}
                onToggleExpand={toggle}
              />
            ))}
          </dl>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wide text-inkMuted mb-1.5">
            {C.draftColumn}
          </div>
          <dl className="space-y-1.5">
            {fields.map((f) => (
              <DiffField
                key={f.field}
                field={f}
                side="draft"
                expanded={expanded}
                onToggleExpand={toggle}
              />
            ))}
          </dl>
        </div>
      </div>

      {/*
        AT-26b / NOTES §10.12. The sentence under the criteria diff, and it is
        NOT decoration: everything else on this screen teaches "a draft is
        waiting", and this is the one place that is also true of something the
        reader will assume is already done.

        The condition is the entity TYPE plus the server's `changed` flag on the
        `criteria` field. It is deliberately NOT "the list got shorter" — and the
        copy is true in both directions for the same reason, so an ADDED
        criterion gets the same correct sentence rather than a wrong one.
      */}
      {item.entity_ref.entity_type === 'rehearsal_template' &&
        criteriaField &&
        approvedCriteriaCount !== null && (
          <p className="mt-2.5 text-[11.5px] text-inkMuted leading-snug">
            <strong className="text-ink">
              {C.criteriaStillGradedLead(approvedCriteriaCount)}
            </strong>{' '}
            {C.criteriaStillGradedRest}
          </p>
        )}
    </>
  );
};

/** The item's header row — badges, label, actor, and the AT-27b marker. */
export const ApprovalItemHeader: React.FC<{ item: ApprovalPendingItem }> = ({ item }) => {
  const kindWord = C.changeKind[item.change_kind];
  const kindClass =
    item.change_kind === 'created'
      ? 'bg-success-light text-success-on'
      : item.change_kind === 'deleted'
        ? 'bg-danger-surface text-danger-text'
        : 'bg-cream-dark text-inkMuted';

  return (
    <>
      <div className="flex items-center gap-2.5 flex-wrap">
        <span
          className={`text-[10px] font-bold rounded-md px-2 py-1 shrink-0 ${itemBadgeClass(item)}`}
        >
          {itemBadgeLabel(item)}
        </span>
        <span
          className={`text-sm font-semibold min-w-0 truncate ${
            item.change_kind === 'deleted' ? 'text-inkMuted' : ''
          }`}
        >
          {item.label}
        </span>
        {/*
          AT-27b. THE badge, and it is the part most likely to be dropped as
          polish. `is_new_since_review` is SERVER-supplied (ADR-B §3.4.1) and is
          rendered, never derived: an edit that bypasses the write chokepoint
          moves the fingerprint and writes no history row at all, so a client-side
          proxy keyed on `history_id`/`changed_at` marks nothing in exactly the
          departing-editor case this feature exists to catch.

          `null` (a plain read, or a 200) is NOT "false" — it means the question
          has no referent — so only an explicit `true` renders.
        */}
        {item.is_new_since_review === true ? (
          <span
            data-testid="new-since-review"
            className="text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 bg-attention-surface text-attention-on shrink-0"
          >
            {C.newSinceOpened}
          </span>
        ) : (
          <span
            className={`text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 ${kindClass}`}
          >
            {kindWord}
          </span>
        )}
        {item.changed_by && item.changed_at && (
          // AT-26c: "Last edited by", never a bare name — history supplies only
          // the LATEST editor, and a bare name beside a two-field diff falsely
          // attributes all of it to one person. `actorDisplayName` renders the
          // EMAIL when `name` is null, never "Unknown".
          <span
            className="ml-auto text-xs text-inkMuted shrink-0"
            title={item.changed_at}
          >
            {C.lastEditedBy(actorDisplayName(item.changed_by), timeAgo(item.changed_at))}
          </span>
        )}
      </div>
      {item.parent_drill_title && (
        <div className="text-[11px] text-inkMuted mt-0.5">
          {C.breadcrumb(item.parent_drill_title)}
        </div>
      )}
    </>
  );
};

function itemBadgeLabel(item: ApprovalPendingItem): string {
  if (item.entity_ref.entity_type === 'question_item') {
    // The editor's own badges, warts included (NOTES §9.3): a queue that badges a
    // question differently from the list it came from is a worse defect than an
    // unsemantic hue.
    return item.kind_label === 'fill_blank' ? 'Fill' : 'MC';
  }
  return entityTerm(item.entity_ref.entity_type);
}

function itemBadgeClass(item: ApprovalPendingItem): string {
  if (item.entity_ref.entity_type === 'question_item') {
    return item.kind_label === 'fill_blank'
      ? 'bg-preview-light text-preview'
      : 'bg-blue-50 text-blue-700';
  }
  switch (item.entity_ref.entity_type) {
    case 'rehearsal_template':
      return 'bg-coral-light text-coral';
    case 'reading_item':
      return 'bg-reading-light text-reading';
    default:
      return 'bg-gray-100 text-inkMuted';
  }
}
